import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { Drug } from '@lib/dictionaries/core/models/drug.interface';
import { DictionariesApiService } from '@lib/dictionaries/core/services/dictionaries-api.service';
import { CodingModel, DrugModel, ServiceKind } from '@lib/guarantees/data-access';
import { TranslatePipe } from '@ngx-translate/core';
import { EMPTY, Observable, Subject, catchError, combineLatest, debounceTime, map, of, switchMap } from 'rxjs';

import { MatTooltip } from '@angular/material/tooltip';

import { OptionListComponent } from '../common/option-list.component';
import { OptionRowDirective } from '../common/option-row.directive';
import { PickedValueComponent } from '../common/picked-value.component';
import { OVERLAY_POSITIONS, SEARCH_DEBOUNCE_MS, SearchFieldComponent, fieldWidth } from '../common/search-field.component';
import { DICTIONARY_PAGE_SIZE, byRelevance } from '../common/search-ranking';
import { ToggleDirective } from '../common/toggle.directive';

/**
 * Two different questions, never one field:
 * - `Substance` — everything sharing an active ingredient, the usual meaning of a drug guarantee.
 * - `Item` — one packaged product by Morion code, when a specific trademark was prescribed.
 */
export enum DrugSearchMode {
  Substance = 'Substance',
  Item = 'Item',
}

export const DRUG_SEARCH_MODES: DrugSearchMode[] = [DrugSearchMode.Substance, DrugSearchMode.Item];

export const DRUG_SEARCH_MODE_LABEL_KEY: Record<DrugSearchMode, string> = {
  [DrugSearchMode.Substance]: 'guaranteeV2.label.drugBySubstance',
  [DrugSearchMode.Item]: 'guaranteeV2.label.drugByCode',
};

export const DRUG_SEARCH_MODE_HINT_KEY: Record<DrugSearchMode, string> = {
  [DrugSearchMode.Substance]: 'guaranteeV2.message.drugBySubstanceHint',
  [DrugSearchMode.Item]: 'guaranteeV2.message.drugByCodeHint',
};

/** A substance row carries its whole group; every other row carries at most itself. */
interface ServiceOption {
  key: string;
  code: string;
  name: string;
  /**
   * What the line is coded as when it differs from what the row shows: a trademark row is found by
   * its Morion code, but the prescription carries the ingredient behind it.
   */
  prescriptionCode?: string;
  /** The forms the group is sold in, which is what the caller offers as a choice. */
  forms?: string[];
  drugs?: DrugModel[];
}

/** A drug carries its products: a code alone cannot be ordered. */
export interface ServiceSelection {
  coding: CodingModel;
  /** Every product the line may be dispensed as. One entry when a specific item was picked. */
  drugs?: DrugModel[];
}

/**
 * Picks a service from the dictionary for this kind. Never let a code be typed: coverage, limits and
 * the partner price list all match on it, so a hand-entered code is a line the engine cannot read.
 *
 * The kind selects the dictionary — a lab test and a consultation are not in the same book
 * — and a medication is looked up two ways, because "this ingredient" and "this exact box" are
 * different prescriptions.
 */
@Component({
  selector: 'guarantees-service-picker',
  standalone: true,
  imports: [CdkConnectedOverlay, CdkOverlayOrigin, MatTooltip, TranslatePipe, OptionListComponent, OptionRowDirective, PickedValueComponent, SearchFieldComponent, ToggleDirective],
  templateUrl: './service-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServicePickerComponent {
  kind = input.required<ServiceKind>();

  /** Pre-selects the service already authored on the line, when amending one. */
  initial = input<CodingModel>();

  selected = output<ServiceSelection>();

  private readonly dictionaries = inject(DictionariesApiService);

  protected readonly overlayPositions = OVERLAY_POSITIONS;

  private readonly origin = viewChild.required(CdkOverlayOrigin);

  protected readonly panelWidth = signal(0);

  /** Every way the list opens comes through here, so it is never shown before it is sized. */
  protected unfold(): void {
    this.panelWidth.set(fieldWidth(this.origin()));
    this.open.set(true);
  }

  protected readonly modes = DRUG_SEARCH_MODES;
  protected readonly modeLabelKey = DRUG_SEARCH_MODE_LABEL_KEY;
  protected readonly modeHintKey = DRUG_SEARCH_MODE_HINT_KEY;

  protected readonly mode = signal<DrugSearchMode>(DrugSearchMode.Substance);
  protected readonly term = signal('');
  protected readonly results = signal<ServiceOption[]>([]);
  protected readonly searching = signal(false);
  protected readonly resolving = signal(false);
  protected readonly open = signal(false);

  /** Only a medication is looked up two ways; every other dictionary has one way in. */
  protected readonly isDrugSearch = computed(() => this.kind() === ServiceKind.Medication);

  private readonly substancePick$ = new Subject<{ coding: CodingModel; option: ServiceOption } | null>();

  /** What the field shows: the chosen service until the health manager types again. */
  private readonly choice = signal<CodingModel | undefined>(undefined);

  /**
   * The caller's coding is the truth where one is bound: the line may be renamed under the picker
   * (free-form), and what the field shows must be what will be written. The internal pick only
   * stands for callers that do not bind one. An empty coding is still an object, so test the fields.
   */
  protected readonly chosen = computed(() => {
    const candidate = this.initial() ?? this.choice();

    return candidate?.code?.trim() || candidate?.name?.trim() ? candidate : undefined;
  });

  /** A substance is its own code: the identifier is not printed twice beside itself. */
  protected readonly chosenName = computed(() => {
    const chosen = this.chosen();
    const code = chosen?.code?.trim() ?? '';
    const name = chosen?.name?.trim() ?? '';

    return code.toLowerCase() === name.toLowerCase() ? '' : name;
  });

  protected showsCode(option: ServiceOption): boolean {
    return option.code.trim().toLowerCase() !== option.name.trim().toLowerCase();
  }

  protected readonly placeholderKey = computed(() => {
    if (this.resolving()) {
      return 'guaranteeV2.message.loadingSubstanceProducts';
    }

    return this.isDrugSearch() && this.mode() === DrugSearchMode.Item ? 'guaranteeV2.message.searchDrugCode' : 'guaranteeV2.message.searchService';
  });

  constructor() {
    // Kind and mode are part of the query: changing either while a term stands must search again.
    // No distinctUntilChanged: `searching` is set at the keystroke, and a term retyped within the
    // debounce would be swallowed by the comparison, leaving the spinner stuck on.
    combineLatest([toObservable(this.term), toObservable(this.kind), toObservable(this.mode)])
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        // A newer keystroke supersedes the request in flight, so a slow page cannot overwrite it.
        switchMap(([term, kind, mode]) =>
          term.trim().length === 0 ? of<ServiceOption[]>([]) : this.search(term.trim(), kind, mode).pipe(catchError(() => of<ServiceOption[]>([])))
        ),
        takeUntilDestroyed()
      )
      .subscribe(items => {
        this.results.set(items);
        this.searching.set(false);
      });

    this.substancePick$
      .pipe(
        switchMap(pick =>
          pick === null
            ? EMPTY
            : this.dictionaries.getDrugsByProtocolIdentifier(pick.option.code).pipe(
                map(drugs => ({ pick, drugs: drugs.map(toDrugModel) })),
                catchError(() => of({ pick, drugs: [] as DrugModel[] }))
              )
        ),
        takeUntilDestroyed()
      )
      .subscribe(({ pick, drugs }) => {
        this.resolving.set(false);
        this.selected.emit({ coding: pick.coding, drugs });
      });

    // A new kind is a new dictionary: the pick, term and results all name the old one.
    effect(() => {
      this.kind();
      this.reset();
    });
  }

  private search(term: string, kind: ServiceKind, mode: DrugSearchMode): Observable<ServiceOption[]> {
    const payload = { searchTerm: term, pageSize: DICTIONARY_PAGE_SIZE, sortBy: 'code', sortDirection: 'Asc' as const };
    const ranked = map((options: ServiceOption[]) => [...options].sort(byRelevance(term, describe)));

    if (kind === ServiceKind.LaboratoryTest) {
      return this.dictionaries.searchLaboratoryServices(payload).pipe(
        map(response => toOptions(response.items)),
        ranked
      );
    }

    if (kind === ServiceKind.Medication && mode === DrugSearchMode.Substance) {
      return this.dictionaries.searchDrugProtocolIdentifiers(term).pipe(map(toSubstanceOptions), ranked);
    }

    if (kind === ServiceKind.Medication) {
      return this.dictionaries.searchDrugs(payload).pipe(
        map(response => (response.items ?? []).map(toItemOption)),
        ranked
      );
    }

    return this.dictionaries.searchAchiCodes(payload).pipe(
      map(response => toOptions(response.items)),
      ranked
    );
  }

  /**
   * The two modes read different columns, so answers from one are not answers in the other —
   * including the standing pick, which is flushed out of the caller rather than left displayed.
   */
  protected setMode(mode: DrugSearchMode): void {
    if (mode === this.mode()) {
      return;
    }

    const hadPick = !!this.chosen();
    this.mode.set(mode);
    this.reset();
    if (hadPick) {
      this.selected.emit({ coding: {}, drugs: [] });
    }
  }

  private reset(): void {
    this.cancelSubstancePick();
    this.choice.set(undefined);
    this.term.set('');
    this.results.set([]);
    this.searching.set(false);
    this.open.set(false);
  }

  private cancelSubstancePick(): void {
    this.substancePick$.next(null);
    this.resolving.set(false);
  }

  protected type(value: string): void {
    this.cancelSubstancePick();
    this.term.set(value);
    // At the keystroke, not after the debounce: an empty list meanwhile must not claim "not found".
    this.searching.set(value.trim().length > 0);
    this.unfold();
  }

  protected choose(option: ServiceOption): void {
    const coding: CodingModel = { code: option.prescriptionCode ?? option.code, name: option.name };

    this.choice.set(coding);
    this.term.set('');
    this.results.set([]);
    this.open.set(false);

    if (this.isDrugSearch() && this.mode() === DrugSearchMode.Substance) {
      this.resolving.set(true);
      this.substancePick$.next({ coding, option });
      return;
    }

    this.selected.emit({ coding, drugs: option.drugs });
  }

  /** Clearing the pick puts the field back to searching rather than leaving a stale code behind. */
  protected clear(): void {
    this.cancelSubstancePick();
    this.choice.set(undefined);
    this.term.set('');
    this.results.set([]);
    this.searching.set(false);
    this.selected.emit({ coding: {}, drugs: [] });
  }
}

/** Every dictionary answers with the same two fields under different names for everything else. */
function toOptions(items: { code: string; nameUa: string; nameEn: string }[] | undefined): ServiceOption[] {
  return (items ?? []).map(item => ({ key: item.code, code: item.code, name: item.nameUa || item.nameEn }));
}

/** What the shared ranker reads a row by. */
function describe(option: ServiceOption) {
  return { code: option.code, name: option.name };
}

function toDrugModel(drug: Drug): DrugModel {
  return {
    code: drug.code,
    nameUa: drug.nameUa,
    protocolIdentifier: drug.protocolIdentifier,
    trademarkFullName: drug.trademarkFullName,
    atcCode: drug.atcCode,
    quantityInPackage: drug.quantityInPackage ?? undefined,
    packagingFactor: drug.packagingFactor ?? undefined,
    formShort: drug.formShort,
    requiresRecipe: drug.requiresRecipe,
    isRecommended: drug.isRecommended,
  };
}

/**
 * One packaged product, found by its own code. The line it writes is still coded by the ingredient —
 * the trademark travels as the sole dispensable product, not as the service.
 */
function toItemOption(drug: Drug): ServiceOption {
  const model = toDrugModel(drug);

  return {
    key: drug.code,
    code: drug.code,
    prescriptionCode: drug.protocolIdentifier?.trim() || drug.code,
    name: drug.trademarkFullName || drug.nameUa || drug.nameEn,
    forms: model.formShort ? [model.formShort] : [],
    drugs: [model],
  };
}

function toSubstanceOptions(identifiers: string[]): ServiceOption[] {
  return identifiers.map(identifier => ({ key: identifier.toLowerCase(), code: identifier, name: identifier }));
}
